import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of, take, switchMap, map } from 'rxjs';
import { ISalonsSearchResultsItem } from '@app/salons-search-results/models/salons-search-results-item.model';
import { ISalonsSearchResults } from '@app/salons-search-results/models/salons-search-results.model';
import { IDepartment, ISalonHeader } from '@app/shared/models';
import { DepartmentService } from '@app/shared/services/department/department.service';
import { find, sortBy } from 'lodash-es';


@Injectable({
  providedIn: 'root'
})
export class SalonsSearchService {

  constructor(
    private departmentService: DepartmentService,
    private http: HttpClient) {
  }

  getSalonsByCityAndDepartment(city: string, departmentName: string): Observable<ISalonsSearchResults> {
    return this.departmentService.getDepartments()
      .pipe(take(1),
        switchMap(departments => {
          const selectedDepartment = find(departments, (z: IDepartment) => z.name === departmentName);
          const url = this.getUrl(city, selectedDepartment);
          return forkJoin([this.http.get<ISalonsSearchResultsItem[]>(url), of(selectedDepartment)]);
        }),
        map(([searchResultsItems, selectedDepartment]) => this.getSalonSearchResults(searchResultsItems, city, selectedDepartment))
      );
  }

  getSalonsByCity(city: string): Observable<ISalonsSearchResults> {
    const url = this.getUrl(city);
    return this.http.get<ISalonsSearchResultsItem[]>(url)
      .pipe(
        map((searchResultsItems: ISalonsSearchResultsItem[]) => this.getSalonSearchResults(searchResultsItems, city))
      );
  }

  getSalonHeaderByCityAndAlias(cityName: string, salonNameAlias: string): Observable<ISalonHeader> {
    return this.http.get<ISalonHeader>(`api/search/city/${cityName}/salon/${salonNameAlias}`);
  }

  private getSalonSearchResults(searchResultsItems: ISalonsSearchResultsItem[], city: string, selectedDepartment: IDepartment = null): ISalonsSearchResults {
    if (searchResultsItems.length) {
      const itemsWithSalonsInSearchedCity = [];
      const itemsWithNoSalonsInSearchedCity = [];
      searchResultsItems.forEach((item) => (item.distanceFromSearchedCity === 0 ? itemsWithSalonsInSearchedCity : itemsWithNoSalonsInSearchedCity).push(item));
      return {
        cityName: city,
        department: selectedDepartment,
        matchesInCityFound: searchResultsItems && itemsWithSalonsInSearchedCity.length > 0,
        matchesNearbyFound: searchResultsItems && itemsWithNoSalonsInSearchedCity.length > 0,
        resultsList: this.sortSalons(itemsWithSalonsInSearchedCity),
        nearbyResultsList: this.sortSalons(itemsWithNoSalonsInSearchedCity)
      };
    }
    return {
      cityName: city,
      department: selectedDepartment,
      matchesInCityFound: false,
      matchesNearbyFound: false,
      resultsList: [],
      nearbyResultsList: []
    };
  }

  private getUrl(city: string, selectedDepartment?: IDepartment): string {
    const urlBase = `api/search/city/${city}`;
    return selectedDepartment ? `${urlBase}/department/${selectedDepartment.id}` : urlBase;
  }

  private sortSalons(searchResultsItems: ISalonsSearchResultsItem[]): ISalonsSearchResultsItem[] {
    return sortBy(searchResultsItems, x => [!x.hasBjootifyPlus, !x.allowsGifts]);
  }
}
